import React, { useMemo, useState } from "react";
import LoadingIndicator from "./LoadingIndicator";
import "./CircularChartCard.css";

/**
 * Card avec graphique circulaire pour afficher des statistiques
 * Utilisé pour "Économies monétaires", "Économies en eau", "Mes dons d'eau", etc.
 */
const CircularChartCard = (props) => {
  const {
    title = "",
    subtitle = "",
    data = { total: 0, details: [] },
    colors = [],
    legendPosition = "bottom", // "bottom" ou "right"
    formatValue = (value) => value?.toString() || "--",
    formatLegendValue = null,
    showLegendPercentage = false,
    legendPercentageInParens = false,
    startRotation = -90, 
    // Si true, le premier segment (au point de départ) est toujours le plus grand.
    // Utile pour éviter qu'un "petit" segment démarre et donne l'impression que le donut tourne à l'envers.
    startWithMaxSegment = false,
    // Si défini, force la couleur du segment ayant la plus grande valeur (total) parmi les segments > 0.
    // Exemple: "#6C8EBF"
    highlightMaxColor = null,
    drawOrder = "normal", // "normal" | "reverse" (uniquement pour le layering du camembert)
    legendOrder = "normal", // "normal" | "reverse" (uniquement pour l'ordre d'affichage de la légende)
    loading = false,
    className = "",
    unit = "",
    centerTextSize = 1, // Multiplicateur pour la taille du texte central (1 = taille par défaut)
    centerTextColor = "#365644", // Couleur du texte central
    centerTextWeight = "500", // Poids du texte central (400 = normal, 500 = medium, 600 = semi-bold, 700 = bold)
    hideLegendValues = false, // Si true, cache les valeurs dans la légende et les affiche dans un tooltip au survol
  } = props || {};
  
  const [hoveredLegendIndex, setHoveredLegendIndex] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const chartData = useMemo(() => {
    if (!data?.details || data.details.length === 0) {
      return { segments: [], allSegments: [], total: data?.total || 0 };
    }

    const total = data.total || 0;
    if (total === 0) {
      return {
        segments: [],
        allSegments: data.details.map((detail, index) => ({
          ...detail,
          color: colors[index % colors.length],
        })),
        total: 0,
      };
    }

    // Filtrer les segments avec total > 0 pour le graphique

    const detailsWithIndex = data.details.map((detail, originalIndex) => ({
      ...detail,
      __originalIndex: originalIndex,
    }));

    // Important: on ne touche pas à l'ordre des données ici (ça impacte la légende).
    // Le "layering" du camembert est géré plus bas au moment du rendu SVG.
    const segmentsWithValue = detailsWithIndex.filter((detail) => detail.total > 0);

    // Construire une map de couleurs par index d'origine (pour garder cohérence donut/legende)
    const highlight = typeof highlightMaxColor === "string" && highlightMaxColor.trim() ? highlightMaxColor : null;
    const palette = Array.isArray(colors) ? colors.filter(Boolean) : [];
    const fallbackPalette = ["#6C8EBF", "#1F3451", "#2D4A75", "#8FAED6"];
    const basePalette = palette.length ? palette : fallbackPalette;
    const paletteWithoutHighlight = highlight ? basePalette.filter((c) => c !== highlight) : basePalette;

    let maxOriginalIndex = null;
    if (highlight && segmentsWithValue.length) {
      maxOriginalIndex = segmentsWithValue.reduce((bestIdx, d) => {
        if (bestIdx == null) return d.__originalIndex;
        const best = segmentsWithValue.find((x) => x.__originalIndex === bestIdx);
        return (d.total ?? 0) > (best?.total ?? 0) ? d.__originalIndex : bestIdx;
      }, null);
    }

    const colorByOriginalIndex = new Map();
    if (highlight && maxOriginalIndex != null) {
      colorByOriginalIndex.set(maxOriginalIndex, highlight);
    }
    // Affecter les autres couleurs dans l'ordre d'origine
    let paletteCursor = 0;
    detailsWithIndex.forEach((d) => {
      const oi = d.__originalIndex;
      if (colorByOriginalIndex.has(oi)) return;
      const c = paletteWithoutHighlight[paletteCursor % Math.max(1, paletteWithoutHighlight.length)];
      paletteCursor += 1;
      colorByOriginalIndex.set(oi, c);
    });

    // Calculer le total des segments avec valeur pour les pourcentages
    const totalWithValue = segmentsWithValue.reduce((sum, detail) => sum + detail.total, 0);

    let currentAngle = startRotation; // Angle de départ (en degrés)
    const segments = segmentsWithValue.map((detail) => {
      const percentage = (detail.total / totalWithValue) * 100;
      const angle = (percentage / 100) * 360;

      const segment = {
        ...detail,
        percentage,
        startAngle: currentAngle,
        endAngle: currentAngle + angle,
        color: colorByOriginalIndex.get(detail.__originalIndex) || basePalette[(detail.__originalIndex ?? 0) % basePalette.length],
      };

      currentAngle += angle;
      return segment;
    });

    // Garder tous les segments (y compris ceux à 0) pour la légende
    const allSegments = data.details.map((detail, index) => ({
      ...detail,
      color: colorByOriginalIndex.get(index) || basePalette[index % basePalette.length],
    }));

    return { segments, allSegments, total };
  }, [data, colors, startRotation, highlightMaxColor]);

  // Dimensions du cercle
  const formatTotalValue = unit ? `${formatValue(chartData.total)}${unit}` : formatValue(chartData.total);

  const estimatedTextWidth = formatTotalValue.length * 9;
  const minRadiusForText = estimatedTextWidth / 2 + 15;
  const baseSize = 140;
  const thickness = 31;
  const availableRadius = (baseSize - thickness) / 2;
  const size = availableRadius < minRadiusForText ? minRadiusForText * 2 + thickness + 20 : baseSize;
  const radius = (size - thickness) / 2;
  const circumference = 2 * Math.PI * radius;
  const center = size / 2;

  // Calculer la fontSize dynamique
  const innerRadius = radius - thickness / 2;
  const maxTextWidth = innerRadius * 1.6;
  const baseFontSize = 18;
  const fontSize = (estimatedTextWidth > maxTextWidth ? Math.max(14, baseFontSize * (maxTextWidth / estimatedTextWidth)) : baseFontSize) * centerTextSize;

  // Calculer les styles pour chaque segment
  const segmentsWithStyle = useMemo(() => {
    if (chartData.segments.length === 0) return [];

    let orderedSegments = chartData.segments;
    if (startWithMaxSegment && orderedSegments.length > 1) {
      const maxIndex = orderedSegments.reduce((bestIdx, s, idx) => {
        if (bestIdx == null) return idx;
        return (Number(s.total || 0) > Number(orderedSegments[bestIdx].total || 0)) ? idx : bestIdx;
      }, null);
      if (maxIndex != null && maxIndex > 0) {
        orderedSegments = [...orderedSegments.slice(maxIndex), ...orderedSegments.slice(0, maxIndex)];
      }
    }

    let cumulativeOffset = 0;
    return orderedSegments.map((segment) => {
      const percentage = segment.percentage / 100;
      const segmentLength = circumference * percentage;
      const gap = circumference - segmentLength;

      const style = {
        strokeDasharray: `${segmentLength} ${gap}`,
        strokeDashoffset: -cumulativeOffset,
      };

      cumulativeOffset += segmentLength;
      return { ...segment, style };
    });
  }, [chartData.segments, circumference, startWithMaxSegment]);

  const renderSegments = useMemo(() => {
    if (!segmentsWithStyle.length) return [];
    return drawOrder === "reverse" ? segmentsWithStyle.slice().reverse() : segmentsWithStyle;
  }, [segmentsWithStyle, drawOrder]);

  const legendSegments = useMemo(() => {
    const segs = chartData.allSegments || [];
    return legendOrder === "reverse" ? segs.slice().reverse() : segs;
  }, [chartData.allSegments, legendOrder]);

  // Fonction pour formater les valeurs de la légende
  const formatLegend = formatLegendValue || formatValue;

  if (loading) {
    return (
      <div className={`circular-chart-card ${className}`}>
        {title && (
          <div className="circular-chart-card__header">
            <h3 className="circular-chart-card__title">{title}</h3>
            {subtitle && <p className="circular-chart-card__subtitle">{subtitle}</p>}
          </div>
        )}
        <div className="circular-chart-card__content">
          <LoadingIndicator />
        </div>
      </div>
    );
  }

  return (
    <div className={`circular-chart-card ${className}`}>
      {title && (
        <div className="circular-chart-card__header">
          <h3 className="circular-chart-card__title">{title}</h3>
          {subtitle && <p className="circular-chart-card__subtitle">{subtitle}</p>}
        </div>
      )}
      <div className={`circular-chart-card__chart circular-chart-card__chart--${legendPosition}`}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="circular-chart-card__svg">
          {/* Cercle de fond (gris clair) */}
          <circle cx={center} cy={center} r={radius} fill="none" stroke="#E0E0E0" strokeWidth={thickness} />

          {/* Segments colorés */}
          {renderSegments.map((segment, index) => (
            <circle
              key={segment.actId || segment.type || index}
              cx={center}
              cy={center}
              r={radius}
              fill="none"
              stroke={segment.color}
              strokeWidth={thickness}
              strokeDasharray={segment.style.strokeDasharray}
              strokeDashoffset={segment.style.strokeDashoffset}
              strokeLinecap="round"
              className="circular-chart-card__segment"
              transform={`rotate(${startRotation} ${center} ${center})`}
            />
          ))}

          {/* Total au centre */}
          <text x={center} y={center} textAnchor="middle" dominantBaseline="middle" className="circular-chart-card__total" fontSize={fontSize * centerTextSize} fontWeight={centerTextWeight} fill={centerTextColor}>
            {formatTotalValue}
          </text>
        </svg>

        {/* Légende */}
        {legendSegments && legendSegments.length > 0 && (
          <div className={`circular-chart-card__legend circular-chart-card__legend--${legendPosition}`}>
            {legendSegments.map((segment, index) => {
              const percentage = chartData.total > 0 ? Math.round((segment.total / chartData.total) * 100) : 0;

              // Si formatLegendValue est fourni et showLegendPercentage est true, utiliser le format personnalisé
              const useCustomFormat = showLegendPercentage && formatLegendValue;
              
              // Formatage de la valeur pour le tooltip
              const formattedValue = formatLegendValue 
                ? formatLegendValue(segment.total, segment, chartData.total)
                : unit 
                  ? `${formatLegend(segment.total)}${unit}`
                  : formatLegend(segment.total);
              
              const tooltipText = hideLegendValues 
                ? `${segment.type}: ${formattedValue}`
                : null;

              const handleMouseEnter = (e) => {
                if (hideLegendValues) {
                  const rect = e.currentTarget.getBoundingClientRect();
                  setTooltipPosition({
                    x: rect.width / 2,
                    y: -10,
                  });
                  setHoveredLegendIndex(index);
                }
              };

              const handleMouseLeave = () => {
                if (hideLegendValues) {
                  setHoveredLegendIndex(null);
                }
              };

              return (
                <div 
                  key={segment.actId || segment.type || index} 
                  className="circular-chart-card__legend-item"
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                  style={{ position: 'relative', cursor: hideLegendValues ? 'pointer' : 'default' }}
                >
                  <div className="circular-chart-card__legend-dot" style={{ backgroundColor: segment.color }} />
                  {useCustomFormat && !hideLegendValues ? (
                    <span className="circular-chart-card__legend-label circular-chart-card__legend-label--custom">
                      <span className="circular-chart-card__legend-name">{segment.type}</span>
                      <span className="circular-chart-card__legend-custom-value"> : {formatLegendValue(segment.total, segment, chartData.total)}</span>
                    </span>
                  ) : (
                    <>
                      <span className="circular-chart-card__legend-label">
                        {segment.type}
                        {!hideLegendValues && showLegendPercentage && (legendPercentageInParens ? ` (${percentage}%)` : ` ${percentage}%`)}
                      </span>
                      {!hideLegendValues && !showLegendPercentage && (
                        <span className="circular-chart-card__legend-value">
                          {formatLegendValue ? formatLegendValue(segment.total, segment, chartData.total) : formatLegend(segment.total)}
                        </span>
                      )}
                    </>
                  )}
                  {hideLegendValues && hoveredLegendIndex === index && (
                    <div 
                      className="circular-chart-card__tooltip"
                      style={{
                        left: `${tooltipPosition.x}px`,
                        top: `${tooltipPosition.y}px`,
                      }}
                    >
                      {tooltipText}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default CircularChartCard;
